import { useState } from 'react'
import { useParams, Navigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import { useLanguage } from '../context/LanguageContext'

const NAVY = '#002E53'
const GOLD = '#C5A059'

const baseInput = (FONT) => ({
  width: '100%', padding: '13px 16px', border: '1.5px solid #dde2ea',
  borderRadius: '10px', fontFamily: FONT, fontSize: '14px', color: NAVY,
  background: '#fff', outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.2s, box-shadow 0.2s',
})

const inputError = { borderColor: '#e53e3e', boxShadow: '0 0 0 3px rgba(229,62,62,0.12)' }

const Label = ({ children, required, FONT, isRTL }) => (
  <label style={{ display: 'block', fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: NAVY, marginBottom: '8px', letterSpacing: '0.2px' }}>
    {children}{required && <span style={{ color: '#e53e3e', marginInlineStart: '3px' }}>*</span>}
  </label>
)

function ServiceForm({ serviceId, serviceData, formLocale, FONT, isRTL }) {
  const [form, setForm] = useState({ fullName: '', email: '', phone: '', propertyDetails: '', message: '' })
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [errors, setErrors] = useState({})

  const validate = () => {
    const errs = {}
    if (!form.fullName.trim()) errs.fullName = formLocale.errors.nameReq
    if (!form.email.trim() || !/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(form.email)) errs.email = formLocale.errors.emailReq
    if (!form.phone.trim()) errs.phone = formLocale.errors.phoneReq
    return errs
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }
    
    setLoading(true)
    try {
      const res = await fetch('/api/admin/services', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form: {
            ...form,
            serviceRequested: serviceData.title,
            serviceId,
          }
        })
      })
      if (!res.ok) throw new Error()
      setSubmitted(true)
    } catch {
      alert(formLocale.errors.failSub)
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <div style={{ background: '#fff', borderRadius: '20px', padding: '60px 40px', textAlign: 'center', boxShadow: '0 8px 40px rgba(0,46,83,0.12)' }}>
        <div style={{ fontSize: '56px', marginBottom: '20px' }}>🎉</div>
        <h3 style={{ fontFamily: FONT, fontSize: '24px', fontWeight: 700, color: NAVY, margin: '0 0 14px' }}>{formLocale.successTitle}</h3>
        <p style={{ fontFamily: FONT, fontSize: '15px', color: '#5a6577', lineHeight: '1.7', margin: '0 0 30px' }}>
          {formLocale.successDesc.replace('{service}', serviceData.title)}
        </p>
        <button onClick={() => { setSubmitted(false); setForm({ fullName: '', email: '', phone: '', propertyDetails: '', message: '' }) }} style={{ background: NAVY, color: '#fff', fontFamily: FONT, fontSize: '15px', fontWeight: 600, padding: '14px 36px', borderRadius: '10px', border: 'none', cursor: 'pointer' }}>
          {formLocale.submitAnother}
        </button>
      </div>
    )
  }

  return (
    <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 2px 18px rgba(0,46,83,0.07)', padding: '40px', borderTop: `4px solid ${GOLD}` }}>
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <h3 style={{ fontFamily: FONT, fontWeight: 700, fontSize: '22px', color: NAVY, margin: '0 0 8px' }}>{serviceData.formTitle}</h3>
        <p style={{ fontFamily: FONT, fontSize: '14px', color: '#6b7a8d', margin: 0 }}>{serviceData.formDesc}</p>
      </div>
      <form onSubmit={handleSubmit} noValidate>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <div>
            <Label required FONT={FONT} isRTL={isRTL}>{formLocale.fullName}</Label>
            <input type="text" placeholder={formLocale.placeholders.name} value={form.fullName} onChange={e => { setForm(p => ({ ...p, fullName: e.target.value })); setErrors(p => ({ ...p, fullName: null })) }} style={{ ...baseInput(FONT), ...(errors.fullName ? inputError : {}) }} />
            {errors.fullName && <p style={{ color: '#e53e3e', fontSize: '12px', fontFamily: FONT, marginTop: '6px', margin: '6px 0 0' }}>{errors.fullName}</p>}
          </div>
          <div>
            <Label required FONT={FONT} isRTL={isRTL}>{formLocale.email}</Label>
            <input type="email" placeholder={formLocale.placeholders.email} value={form.email} onChange={e => { setForm(p => ({ ...p, email: e.target.value })); setErrors(p => ({ ...p, email: null })) }} style={{ ...baseInput(FONT), ...(errors.email ? inputError : {}) }} />
            {errors.email && <p style={{ color: '#e53e3e', fontSize: '12px', fontFamily: FONT, marginTop: '6px', margin: '6px 0 0' }}>{errors.email}</p>}
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <Label required FONT={FONT} isRTL={isRTL}>{formLocale.phone}</Label>
            <input type="tel" placeholder={formLocale.placeholders.phone} value={form.phone} onChange={e => { setForm(p => ({ ...p, phone: e.target.value })); setErrors(p => ({ ...p, phone: null })) }} style={{ ...baseInput(FONT), ...(errors.phone ? inputError : {}) }} />
            {errors.phone && <p style={{ color: '#e53e3e', fontSize: '12px', fontFamily: FONT, marginTop: '6px', margin: '6px 0 0' }}>{errors.phone}</p>}
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <Label FONT={FONT} isRTL={isRTL}>{formLocale.detailsOpt}</Label>
            <input type="text" placeholder={formLocale.placeholders.details} value={form.propertyDetails} onChange={e => setForm(p => ({ ...p, propertyDetails: e.target.value }))} style={baseInput(FONT)} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <Label FONT={FONT} isRTL={isRTL}>{formLocale.messageOpt}</Label>
            <textarea rows={4} placeholder={formLocale.placeholders.message} value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} style={{ ...baseInput(FONT), resize: 'vertical' }} />
          </div>
        </div>
        <button type="submit" disabled={loading} style={{ width: '100%', background: loading ? '#8a9ab0' : NAVY, color: '#fff', fontFamily: FONT, fontSize: '16px', fontWeight: 700, padding: '16px', borderRadius: '12px', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.25s', boxShadow: loading ? 'none' : `0 8px 24px rgba(0,46,83,0.3)`, letterSpacing: '0.3px', marginTop: '10px' }}
          onMouseEnter={e => { if (!loading) e.currentTarget.style.transform = 'translateY(-2px)' }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)' }}>
          {loading ? formLocale.submitting : formLocale.submitRequest}
        </button>
      </form>
    </div>
  )
}

export default function ServicePage() {
  const { id } = useParams()
  const { t, isRTL, langMeta } = useLanguage()
  const FONT = `"${langMeta.font}", sans-serif`
  
  const validIds = ['holiday-homes', 'property-management', 'fit-out', 'legal-assistance']
  if (!validIds.includes(id)) {
    return <Navigate to="/" replace />
  }

  const sData = t('servicesPage')
  const serviceData = sData.services[id]

  // The emoji icons can be hardcoded here per id, or we attach them locally
  const getIcon = (key) => {
    if (key === 'holiday-homes') return '🏝️'
    if (key === 'property-management') return '🏢'
    if (key === 'fit-out') return '✨'
    if (key === 'legal-assistance') return '⚖️'
    return '📋'
  }

  return (
    <div style={{ fontFamily: FONT }} dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar />

      {/* ── Hero ── */}
      <div style={{
        background: `linear-gradient(135deg, ${NAVY} 0%, #001a33 60%, #0a2d4a 100%)`,
        padding: '150px 20px 80px', textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: '-80px', right: isRTL ? 'auto' : '-80px', left: isRTL ? '-80px' : 'auto', width: '380px', height: '380px', borderRadius: '50%', background: `${GOLD}15`, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-60px', left: isRTL ? 'auto' : '-60px', right: isRTL ? '-60px' : 'auto', width: '260px', height: '260px', borderRadius: '50%', background: `${GOLD}10`, pointerEvents: 'none' }} />

        <span style={{ display: 'inline-block', background: `${GOLD}20`, color: GOLD, fontFamily: FONT, fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px', padding: '6px 18px', borderRadius: '50px', marginBottom: '18px', textTransform: 'uppercase', border: `1px solid ${GOLD}40` }}>
          {sData.heroTag}
        </span>
        <h1 style={{ fontFamily: FONT, fontSize: 'clamp(30px, 4vw, 52px)', fontWeight: 700, color: '#fff', margin: '0 0 16px', letterSpacing: '-0.5px', lineHeight: '1.2' }}>
          {serviceData.title}
        </h1>
        <p style={{ fontFamily: FONT, fontSize: '16px', color: 'rgba(255,255,255,0.72)', maxWidth: '580px', margin: '0 auto', lineHeight: '1.7' }}>
          {serviceData.heroSub}
        </p>
      </div>

      <div style={{ background: '#f4f7fb', padding: '80px 20px' }}>
        <div className="service-page-content" style={{ maxWidth: '1000px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr)', gap: '40px', alignItems: 'flex-start' }}>
          
          {/* Content Left */}
          <div className="service-page-left" style={{ paddingInlineEnd: '20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>{getIcon(id)}</div>
            <h2 style={{ fontFamily: FONT, fontSize: '28px', fontWeight: 700, color: NAVY, margin: '0 0 16px' }}>
              {sData.whyChoose.replace('{service}', serviceData.title)}
            </h2>
            <p style={{ fontFamily: FONT, fontSize: '16px', color: '#5a6577', lineHeight: '1.8', margin: '0 0 16px' }}>
              {serviceData.desc}
            </p>
            {serviceData.extraDesc && (
              <p style={{ fontFamily: FONT, fontSize: '15px', color: '#6d7a8c', lineHeight: '1.8', margin: '0 0 28px' }}>
                {serviceData.extraDesc}
              </p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {(serviceData.points || []).map((point, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: `${GOLD}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: GOLD, fontSize: '12px', flexShrink: 0 }}>✓</div>
                  <span style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 500, color: NAVY }}>{point}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Form Right */}
          <ServiceForm serviceId={id} serviceData={serviceData} formLocale={sData.form} FONT={FONT} isRTL={isRTL} />
          
        </div>
      </div>

      <style>{`
        @media (max-width: 860px) {
          .service-page-content {
            grid-template-columns: 1fr !important;
          }
          .service-page-left {
            padding-inline-end: 0 !important;
            margin-bottom: 20px;
          }
        }
      `}</style>
      
      {/* Script injected to fix media queries on inline styles natively */}
      <div className="service-page-style-fix" style={{ display: 'none' }} />

      <Footer />
    </div>
  )
}
